import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CircleNotch, CheckCircle, XCircle, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '—');

const AdminServiceProposalsPage = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/service-proposals?status=${status}`);
      setItems(r.data?.items || []);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const act = async (id, action) => {
    try {
      await api.post(`/api/admin/service-proposals/${id}/${action}`,
        action === 'reject' ? { note: notes[id] || '' } : {});
      toast.success(action === 'approve' ? 'Prestazione aggiunta al catalogo' : 'Proposta rifiutata');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operazione non riuscita');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="admin-service-proposals-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-2xl text-foreground">Prestazioni proposte</h2>
          <p className="text-sm text-foreground/60 mt-1">
            Prestazioni suggerite dai professionisti e non ancora presenti nel catalogo nazionale.
          </p>
        </div>
        <div className="flex gap-2">
          {[['pending', 'In attesa'], ['approved', 'Approvate'], ['rejected', 'Rifiutate'], ['all', 'Tutte']].map(([k, label]) => (
            <Button
              key={k} size="sm" variant={status === k ? 'default' : 'outline'}
              className="rounded-full h-8" onClick={() => setStatus(k)}
              data-testid={`proposals-filter-${k}`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl p-10 text-center" data-testid="proposals-empty">
          <CheckCircle size={44} weight="duotone" className="text-primary mx-auto mb-3" />
          <p className="text-foreground/70">Nessuna proposta in questo stato.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <Card key={p.proposal_id} className="rounded-2xl p-4" data-testid={`proposal-${p.proposal_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium flex items-center gap-1.5">
                    <Tag size={16} weight="duotone" className="text-primary" />
                    {p.name}
                    {p.price_eur != null && <span className="text-foreground/60 text-sm">· €{p.price_eur}</span>}
                  </p>
                  <p className="text-xs text-foreground/60 mt-1">
                    {p.professional_name} ({p.professional_email}) · professione: {p.profession_code || '—'} · {fmt(p.created_at)}
                  </p>
                  {p.description && <p className="text-sm text-foreground/70 mt-2">{p.description}</p>}
                  {p.admin_note && <p className="text-xs text-foreground/60 mt-1">Nota: {p.admin_note}</p>}
                </div>
                {p.status === 'pending' ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      className="h-9 w-48" placeholder="Nota (se rifiuti)"
                      value={notes[p.proposal_id] || ''}
                      onChange={(e) => setNotes((n) => ({ ...n, [p.proposal_id]: e.target.value }))}
                      data-testid={`proposal-note-${p.proposal_id}`}
                    />
                    <Button size="sm" className="rounded-full h-9" onClick={() => act(p.proposal_id, 'approve')} data-testid={`proposal-approve-${p.proposal_id}`}>
                      <CheckCircle size={15} className="mr-1.5" /> Approva
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full h-9" onClick={() => act(p.proposal_id, 'reject')} data-testid={`proposal-reject-${p.proposal_id}`}>
                      <XCircle size={15} className="mr-1.5" /> Rifiuta
                    </Button>
                  </div>
                ) : (
                  <Badge variant="outline" className="text-[11px]">
                    {p.status === 'approved' ? 'Approvata' : 'Rifiutata'} il {fmt(p.reviewed_at)}
                  </Badge>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminServiceProposalsPage;
